import { io, type Socket } from 'socket.io-client'
import { ClockEstimator } from '../../shared/clock.js'
import { encodeInput } from '../../shared/inputcodec.js'
import { PROTOCOL_VERSION, type DisclordView, type FeedbackTag, type LinkCodeMsg, type LinkResultMsg, type PartyStateMsg, type RunStateMsg, type SlotResultMsg, type InputMsg, type JoinedMsg, type KioskMsg, type MatchResultMsg, type PauseMsg, type PongMsg, type QueueStateMsg, type ResumeMsg, type ResyncMsg, type RoomsMsg, type SnapshotMsg, type TrainingBotMsg, type WelcomeMsg } from '../../shared/protocol.js'
import { CLOCK_SYNC_INTERVAL_S, TICK_RATE } from '../../sim/constants.js'
import type { PlayerInput } from '../../sim/types.js'
import { NetLink } from '../../tools/netsim.js'

const TOKEN_KEY = 'disclike.token.v1'
const OLD_TOKEN_KEY = 'disclike.token'

export interface NetEvents {
  welcome: (w: WelcomeMsg) => void
  snapshot: (s: SnapshotMsg) => void
  resync: (r: ResyncMsg) => void
  joined: (j: JoinedMsg) => void
  rooms: (r: RoomsMsg) => void
  matchResult: (m: MatchResultMsg) => void
  queueState: (q: QueueStateMsg) => void
  kiosk: (k: KioskMsg) => void
  pause: (p: PauseMsg) => void
  resume: (r: ResumeMsg) => void
  spectateFull: (r: { roomId: string }) => void
  idleWarning: (r: { kickInMs: number }) => void
  sessionReplaced: () => void
  vsBotDenied: () => void
  nameResult: (r: { ok: boolean; shown: string }) => void
  kicked: (reason: string) => void
  disconnect: () => void
  /** Socket came back after a drop and the hello succeeded again. */
  reconnected: (w: WelcomeMsg) => void
  trainingDenied: () => void
  /** 10 §7 drain: the server is restarting; socket.io auto-reconnects. */
  serverRestart: () => void
  /** 10 §2 party membership changed (Sprint 7). */
  partyState: (p: PartyStateMsg) => void
  /** 12 §1 disclord view (Sprint 8): on hello and after every recorded match. */
  disclord: (d: DisclordView) => void
  /** 12 §2 slot write result (Sprint 8). */
  slotResult: (r: SlotResultMsg) => void
  /** 12 §7.4 device linking (Sprint 8). */
  linkCode: (c: LinkCodeMsg) => void
  linkResult: (r: LinkResultMsg) => void
  /** 12 §4 runs (Sprint 9). */
  runState: (r: RunStateMsg) => void
  runDenied: () => void
}

export interface NetShaping { latency: number; jitter: number; loss: number }

/** Socket wrapper: hello + stored token, ping/pong clock sync, typed fan-out,
 *  optional both-way network shaping (`?lag=&jitter=&loss=`, 14 R10). */
export class NetClient {
  readonly socket: Socket
  readonly clock = new ClockEstimator()
  private handlers: Partial<NetEvents> = {}
  private lastPong: { s2: number; at: number } | null = null
  private pingTimer: ReturnType<typeof setInterval> | null = null
  private up: NetLink | null = null
  private down: NetLink | null = null
  snapshotsReceived = 0
  lastSnapshotAt = 0
  lastSnapshotTick = 0
  bytesIn = 0
  inputsSent = 0
  /** Server tick at the last pong, with the local time it was observed. */
  private tickAnchor: { tick: number; at: number } | null = null

  constructor(url?: string, shaping?: NetShaping | null) {
    this.socket = io(url ?? '/', { transports: ['websocket'], autoConnect: false })
    if (shaping && (shaping.latency || shaping.jitter || shaping.loss)) {
      this.up = new NetLink({ ...shaping, seed: 11 })
      this.down = new NetLink({ ...shaping, seed: 23 })
    }
    const on = <T>(ev: string, fn: (m: T) => void) => {
      this.socket.on(ev, (m: T) => { if (this.down) this.down.send(() => fn(m)); else fn(m) })
    }
    on<SnapshotMsg>('snapshot', (s) => {
      this.snapshotsReceived++
      this.lastSnapshotAt = performance.now()
      this.lastSnapshotTick = s.tick
      this.bytesIn += JSON.stringify(s).length
      this.handlers.snapshot?.(s)
    })
    on<PongMsg>('pong', (p) => {
      const now = performance.now()
      this.clock.addSample(p.t, p.s, now)
      this.lastPong = { s2: p.s2, at: now }
      this.tickAnchor = { tick: p.tick, at: now - this.clock.rtt / 2 }
    })
    on<ResyncMsg>('resync', (r) => { this.tickAnchor = { tick: r.serverTick, at: performance.now() - this.clock.rtt / 2 }; this.handlers.resync?.(r) })
    on<JoinedMsg>('joined', (j) => this.handlers.joined?.(j))
    on<RoomsMsg>('rooms', (r) => this.handlers.rooms?.(r))
    on<MatchResultMsg>('matchResult', (m) => this.handlers.matchResult?.(m))
    on<QueueStateMsg>('queueState', (q) => this.handlers.queueState?.(q))
    on<KioskMsg>('kiosk', (k) => this.handlers.kiosk?.(k))
    on<PauseMsg>('pause', (p) => this.handlers.pause?.(p))
    on<ResumeMsg>('resume', (r) => this.handlers.resume?.(r))
    on<{ roomId: string }>('spectateFull', (r) => this.handlers.spectateFull?.(r))
    on<{ kickInMs: number }>('idleWarning', (r) => this.handlers.idleWarning?.(r))
    on<{ ok: boolean; shown: string }>('nameResult', (r) => this.handlers.nameResult?.(r))
    on<{ newToken: string }>('authRotate', (r) => localStorage.setItem(TOKEN_KEY, r.newToken))
    on<unknown>('vsBotDenied', () => this.handlers.vsBotDenied?.())
    on<unknown>('trainingDenied', () => this.handlers.trainingDenied?.())
    on<unknown>('server.restart', () => this.handlers.serverRestart?.())
    on<PartyStateMsg>('partyState', (p) => this.handlers.partyState?.(p))
    on<DisclordView>('disclord', (d) => this.handlers.disclord?.(d))
    on<SlotResultMsg>('slotResult', (r) => this.handlers.slotResult?.(r))
    on<LinkCodeMsg>('linkCode', (c) => this.handlers.linkCode?.(c))
    on<LinkResultMsg>('linkResult', (r) => this.handlers.linkResult?.(r))
    on<RunStateMsg>('runState', (r) => this.handlers.runState?.(r))
    on<unknown>('runDenied', () => this.handlers.runDenied?.())
    on<unknown>('sessionReplaced', () => { this.replaced = true; this.handlers.sessionReplaced?.() })
    on<string>('kicked', (reason) => this.handlers.kicked?.(reason))
    this.socket.on('disconnect', () => { this.stopPing(); this.inputsOpen = false; this.handlers.disconnect?.() })
    // 10 §6: socket.io auto-reconnect; on success re-hello with the stored token
    // so the server can match the seat and send a full snapshot.
    this.socket.on('connect', () => { if (this.helloDone && !this.replaced) this.hello({}).then((w) => this.handlers.reconnected?.(w)).catch(() => {}) })
  }

  private helloDone = false
  /** Inputs flow only between a completed hello and the next disconnect: records from the previous
   *  connection's predictor must not leak onto a fresh session (they would set the server's ack high). */
  private inputsOpen = false
  replaced = false
  private hello(opts: { name?: string; spectate?: string }): Promise<WelcomeMsg> {
    return new Promise((resolve, reject) => {
      let token = localStorage.getItem(TOKEN_KEY) ?? undefined
      if (!token) { const old = localStorage.getItem(OLD_TOKEN_KEY); if (old) { token = old; localStorage.setItem(TOKEN_KEY, old); localStorage.removeItem(OLD_TOKEN_KEY) } }
      this.socket.emit('hello', { v: PROTOCOL_VERSION, token, name: opts.name, spectate: opts.spectate }, (r: WelcomeMsg | { ok: false; error: string }) => {
        if (!r.ok) return reject(new Error(r.error))
        localStorage.setItem(TOKEN_KEY, r.token)
        this.tickAnchor = { tick: r.serverTick, at: performance.now() }
        this.helloDone = true
        this.inputsOpen = true
        this.startPing()
        resolve(r)
      })
    })
  }

  /** After SIGNED IN ELSEWHERE: reclaim the session here. */
  reclaim(): void { this.replaced = false; if (this.socket.connected) this.hello({}).then((w) => this.handlers.reconnected?.(w)).catch(() => {}); else this.socket.connect() }

  on<K extends keyof NetEvents>(ev: K, fn: NetEvents[K]): void { this.handlers[ev] = fn }

  private emit(ev: string, ...args: unknown[]): void {
    if (this.up) this.up.send(() => this.socket.emit(ev, ...args))
    else this.socket.emit(ev, ...args)
  }

  /** Spec §5 LINK step: open the transport without the hello. Resolves once connected; a later
   *  `connect()` then hellos on the open socket. Rejects only on a fatal connect_error. */
  open(): Promise<void> {
    if (this.socket.connected) return Promise.resolve()
    return new Promise((resolve, reject) => {
      this.socket.once('connect', () => resolve())
      this.socket.once('connect_error', (e: Error) => reject(e))
      this.socket.connect()
    })
  }

  connect(opts: { name?: string; spectate?: string } = {}): Promise<WelcomeMsg> {
    const go = () => this.hello(opts).then((w) => { this.handlers.welcome?.(w); return w })
    if (this.socket.connected) return go()
    return new Promise((resolve, reject) => {
      this.socket.once('connect', () => go().then(resolve, reject))
      this.socket.connect()
    })
  }

  private startPing(): void {
    this.stopPing()
    const send = () => {
      const now = performance.now()
      const ack = this.lastPong ? { s: this.lastPong.s2, hold: now - this.lastPong.at } : undefined
      this.emit('ping', { t: now, ack })
    }
    for (let i = 0; i < 4; i++) setTimeout(send, i * 150)
    this.pingTimer = setInterval(send, CLOCK_SYNC_INTERVAL_S * 1000)
  }
  private stopPing(): void { if (this.pingTimer) clearInterval(this.pingTimer); this.pingTimer = null }

  sendInputs(inputs: PlayerInput[]): void {
    if (!inputs.length || !this.inputsOpen) return
    const msg: InputMsg = { inputs: inputs.map(encodeInput) }
    this.inputsSent++
    if (this.up) this.up.send(() => this.socket.volatile.emit('input', msg))
    else this.socket.volatile.emit('input', msg)
  }

  setQueue(want: boolean): void { this.emit('queue', { want }) }
  lobbyPick(ids: string[]): void { this.emit('lobby:pick', { ids }) }
  lobbyLock(id: string): void { this.emit('lobby:lock', { id }) }
  loadout(ids: string[]): void { this.emit('loadout', { ids }) }
  spectate(roomId: string | null): void { this.emit('spectate', roomId) }
  rematch(want: boolean): void { this.emit('rematch', { want }) }
  vsBot(tier?: 'ROOKIE' | 'PRO' | 'ACE'): void { this.emit('vsBot', tier ? { tier } : {}) }
  setName(name: string): void { this.emit('setName', { name }) }
  /** 13 §2 playtest marker. */
  feedback(tag: FeedbackTag): void { this.emit('feedback', { tag }) }
  /** 12 §4 runs. */
  runStart(tier = 1): void { this.emit('run:start', { tier }) }
  runCard(id: string): void { this.emit('run:card', { id }) }
  /** 12 §7.4 device linking. */
  linkIssue(): void { this.emit('link:issue') }
  linkRedeem(code: string): void { this.emit('link:redeem', { code }) }
  /** 12 §2 imprint slot write. */
  slot(index: 2 | 3, id: string | null): void { this.emit('slot', { index, id }) }
  /** 10 §2 party: a 4-char code joins/creates; null leaves. */
  party(code: string | null): void { this.emit('party', { code }) }
  resetIdentity(): void { this.emit('resetIdentity') }
  requestResync(): void { this.resyncs++; this.lastResyncAt = performance.now(); this.emit('resync') }
  /** 09 §5 training lane. */
  training(want: boolean): void { this.emit('training', { want }) }
  trainingBot(msg: TrainingBotMsg): void { this.emit('training:bot', msg) }
  resyncs = 0
  lastResyncAt = 0

  serverNow(): number { return this.clock.toServerTime(performance.now()) }
  get rtt(): number { return this.clock.rtt }

  /** Estimated current server tick (from the last pong/resync anchor). */
  get serverTick(): number {
    if (!this.tickAnchor) return this.lastSnapshotTick
    return this.tickAnchor.tick + Math.floor(((performance.now() - this.tickAnchor.at) / 1000) * TICK_RATE)
  }
}
